import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { createGlobalStyle } from 'styled-components';

import { RoutePath } from '@quizzach/interfaces';
import {
	IcArrowRight,
	IcBasket,
	IcCards,
	IcGear,
	IcGoogleG,
	IcKjupitnik,
	IcMail,
	IcMenuLines,
	IcPlus,
	IcPower,
	IcUserRound,
} from '@quizzach/assets';
import { MobileNavBar, MobileSheet, SHELL_BREAK } from '@quizzach/styles';
import { loadGoogleScript, scrollToTop } from '@quizzach/utils';
import { useAuth, useCartContext } from '@quizzach/contexts';
import { useNotifications } from '@quizzach/hooks';
import { ConfirmationModal } from '@quizzach/components';

// Keeps page content clear of the fixed bottom bar on touch widths.
// --bottom-nav-h is the bar's rendered height; sticky bars (KBar) offset
// against the same variable so they sit flush on top of the nav.
const BottomBarClearance = createGlobalStyle`
	${SHELL_BREAK} {
		body {
			--bottom-nav-h: 63px;
			padding-bottom: calc(var(--bottom-nav-h) + env(safe-area-inset-bottom));
		}
	}
`;

interface SheetRowProps {
	title: string;
	subtitle?: string;
	onClick: () => void;
}

const SheetRow = ({ title, subtitle, onClick }: SheetRowProps) => (
	<button type='button' className='row' onClick={onClick}>
		<span>
			<b>{title}</b>
			{subtitle && <u>{subtitle}</u>}
		</span>
		<span className='arr'>
			<IcArrowRight size={17} />
		</span>
	</button>
);

export const BottomNav = () => {
	const { t } = useTranslation();
	const location = useLocation();
	const navigate = useNavigate();
	const { isAuthenticated, profileInfo, handleLogout } = useAuth();
	const { cartCount } = useCartContext();
	const [showCreateMenu, setShowCreateMenu] = useState(false);
	const [showMoreMenu, setShowMoreMenu] = useState(false);
	const [showLogoutModal, setShowLogoutModal] = useState(false);
	const { notifications, dismissNotification } = useNotifications();

	// M10-D: the invisible GIS button is stretched over the visual Google
	// pill; same mechanism as the auth page.
	const gwrapRef = useRef<HTMLDivElement>(null);
	const [gisScale, setGisScale] = useState('');

	// the admin question/answer preview replicates the play stage, so it
	// drops the app bottom bar the same way the play routes do
	const isQuizMode = useMemo(
		() =>
			location.pathname.startsWith('/quiz/') ||
			location.pathname.startsWith('/preview/'),
		[location.pathname]
	);
	const isAnyMenuOpen = showCreateMenu || showMoreMenu;
	const showAuthPills = showMoreMenu && isAuthenticated === false;

	const clearOverlayHistoryMarker = () => {
		const currentState = window.history.state ?? {};
		if (!currentState?.qzBottomNavOverlay) return;
		const nextState = { ...currentState };
		delete nextState.qzBottomNavOverlay;
		window.history.replaceState(nextState, '');
	};

	const openOverlayMenu = (menu: 'create' | 'more') => {
		// ensure we don't stack multiple overlay markers
		clearOverlayHistoryMarker();
		const currentState = window.history.state ?? {};
		window.history.pushState({ ...currentState, qzBottomNavOverlay: menu }, '');

		if (menu === 'create') {
			setShowMoreMenu(false);
			setShowCreateMenu(true);
		} else {
			setShowCreateMenu(false);
			setShowMoreMenu(true);
		}
	};

	const closeOverlayMenus = () => {
		setShowCreateMenu(false);
		setShowMoreMenu(false);
		clearOverlayHistoryMarker();
	};

	const go = (path: string) => {
		closeOverlayMenus();
		navigate(path);
		scrollToTop();
	};

	useEffect(() => {
		if (isQuizMode || !isAnyMenuOpen) return;

		// Mobile/browser back button should close the overlay first.
		const onPopState = () => {
			setShowCreateMenu(false);
			setShowMoreMenu(false);
			clearOverlayHistoryMarker();
		};

		window.addEventListener('popstate', onPopState);
		return () => window.removeEventListener('popstate', onPopState);
	}, [isAnyMenuOpen, isQuizMode]);

	useEffect(() => {
		if (!showAuthPills) return;
		// Each open appends a fresh GIS script whose load re-scans the DOM and
		// renders into the freshly mounted g_id_signin (the auth page relies
		// on the same per-mount rescan). The google-auth landing page reads
		// this referer to return the user where they were.
		sessionStorage.setItem('referer', location.pathname);
		loadGoogleScript();
	}, [showAuthPills, location.pathname]);

	useEffect(() => {
		if (!showAuthPills) return;
		let timer: number;
		let tries = 0;
		// layout offset of the GIS button inside .gis (offset chain is
		// transform-independent, so re-runs measure the same values)
		const offsetWithin = (el: HTMLElement, root: HTMLElement) => {
			let x = 0;
			let y = 0;
			let node: HTMLElement | null = el;
			while (node && node !== root) {
				x += node.offsetLeft;
				y += node.offsetTop;
				node = node.offsetParent as HTMLElement | null;
			}
			return { x, y };
		};
		const fit = () => {
			const wrap = gwrapRef.current;
			const gis = wrap?.querySelector<HTMLElement>('.gis');
			const pill = wrap?.querySelector<HTMLElement>('.gpill');
			const target =
				gis?.querySelector<HTMLElement>('div[role=button]') ||
				gis?.querySelector<HTMLElement>('iframe');
			if (gis && pill && target && target.offsetWidth > 0) {
				const sx = pill.offsetWidth / target.offsetWidth;
				const sy = pill.offsetHeight / target.offsetHeight;
				const { x, y } = offsetWithin(target, gis);
				setGisScale(
					`translate(${-x * sx}px, ${-y * sy}px) scale(${sx}, ${sy})`
				);
				return;
			}
			if (tries++ < 80) timer = window.setTimeout(fit, 125);
		};
		fit();
		return () => window.clearTimeout(timer);
	}, [showAuthPills]);

	const requestLogout = () => {
		closeOverlayMenus();
		setShowLogoutModal(true);
	};

	const handleConfirmLogout = (confirm: boolean) => {
		setShowLogoutModal(false);
		if (confirm) {
			navigate(RoutePath.Login);
			handleLogout();
		}
	};

	if (isQuizMode) return null;

	const isCartRouteActive =
		location.pathname === RoutePath.Summary ||
		location.pathname === RoutePath.QuickSummary ||
		location.pathname === RoutePath.GeneratorSummary;
	const isProfileRouteActive = location.pathname.startsWith(RoutePath.Profile);

	const isCartActive = !isAnyMenuOpen && isCartRouteActive;
	const isProfileActive = !isAnyMenuOpen && isProfileRouteActive;
	const isHomeActive =
		!isAnyMenuOpen && location.pathname === RoutePath.Homepage;

	return (
		<>
			<BottomBarClearance />
			<ConfirmationModal
				onConfirm={handleConfirmLogout}
				show={showLogoutModal}
				text={t('profile.userInfo.logoutConfirm')}
			/>

			{showCreateMenu && (
				<MobileSheet
					$roomyRows
					role='dialog'
					aria-modal='true'
					aria-label={t('nav.createQuizMenu')}
					onClick={closeOverlayMenus}
				>
					<div className='in' onClick={(e) => e.stopPropagation()}>
						<SheetRow
							title={t('nav.quickPick')}
							subtitle={t('nav.quickPickDescription')}
							onClick={() => go(RoutePath.QuickPick)}
						/>
						<SheetRow
							title={t('nav.generator')}
							subtitle={t('nav.generatorDescription')}
							onClick={() => go(RoutePath.GeneratorCustomizer)}
						/>
						<SheetRow
							title={t('nav.premade')}
							subtitle={t('nav.premadeDescription')}
							onClick={() => go(RoutePath.PremadeQuizzes)}
						/>
					</div>
				</MobileSheet>
			)}

			{showMoreMenu && (
				<MobileSheet
					role='dialog'
					aria-modal='true'
					aria-label={t('nav.moreMenu')}
					onClick={closeOverlayMenus}
				>
					<div className='in' onClick={(e) => e.stopPropagation()}>
						{isAuthenticated && profileInfo && (
							<>
								<div className='acct'>
									<button
										type='button'
										className='alink'
										onClick={() => go(RoutePath.Profile)}
									>
										<span className='aav'>
											{profileInfo.profile_picture ? (
												<img
													src={`/avatars/${profileInfo.profile_picture}.webp`}
													alt=''
												/>
											) : (
												<IcUserRound size={20} />
											)}
										</span>
										<span className='anick'>@{profileInfo.nickname}</span>
									</button>
								</div>
								{/* the gold notification card separates itself - the
								    hairline only returns when there is no card */}
								{notifications.length === 0 && <div className='asep' />}
							</>
						)}

						{isAuthenticated === false && (
							<>
								<div className='authpills'>
									<button
										type='button'
										className='ghostpill'
										onClick={() => go(RoutePath.Login)}
									>
										{t('modal.auth.loginRegister')}
									</button>
									<div className='gwrap' ref={gwrapRef}>
										<span className='gpill' aria-hidden>
											<IcGoogleG size={18} />
											{t('auth.googleButton')}
										</span>
										<div
											className='gis'
											style={gisScale ? { transform: gisScale } : undefined}
										>
											<div
												id='g_id_onload'
												data-client_id='262018644453-8h0kcatg85os3bd3qaeik1e62jbiovuf.apps.googleusercontent.com'
												data-context='use'
												data-ux_mode='redirect'
												data-login_uri='https://quizzach.com/google-auth'
												data-auto_prompt='false'
											></div>
											<div
												className='g_id_signin'
												data-type='standard'
												data-shape='pill'
												data-theme='outline'
												data-text='continue_with'
												data-size='large'
												data-locale='hr'
												data-logo_alignment='left'
												data-width={320}
											></div>
										</div>
									</div>
								</div>
								<div className='asep' />
							</>
						)}

						{/* notification preview (founder pick B2 + C4 gold-note card,
						    which separates itself - no hairline after); logged-in
						    only, and hidden entirely when there is nothing to say */}
						{isAuthenticated && notifications.length > 0 && (
							<div className='notifs'>
								<span className='ncap'>{t('nav.notifications')}</span>
								{notifications.slice(0, 2).map((notification) => {
									const { path } = notification;
									const content = (
										<>
											<b>{t(notification.titleKey)}</b>
											{notification.bodyKey && <u>{t(notification.bodyKey)}</u>}
										</>
									);
									return path ? (
										<button
											key={notification.id}
											type='button'
											className='nr'
											onClick={() => {
												dismissNotification(notification.id);
												go(path);
											}}
										>
											{content}
										</button>
									) : (
										<div key={notification.id} className='nr'>
											{content}
										</div>
									);
								})}
							</div>
						)}

						<SheetRow
							title={t('nav.giftVoucher')}
							onClick={() => go(RoutePath.Voucher)}
						/>
						<SheetRow
							title={t('nav.infoCenter')}
							onClick={() => go(RoutePath.Info)}
						/>
						{/* bottom utility strip: contact mail + (logged-in only)
						    settings and logout, relocated from the account row */}
						<div className='foot'>
							{isAuthenticated && (
								<>
									<button
										type='button'
										className='circ'
										aria-label={t('profile.redesign.logout')}
										onClick={requestLogout}
									>
										<IcPower size={16} />
									</button>
									<button
										type='button'
										className='circ'
										aria-label={t('profile.tabs.settings')}
										onClick={() =>
											go(`${RoutePath.Profile}/${RoutePath.ProfileSettings}`)
										}
									>
										<IcGear size={16} />
									</button>
								</>
							)}
							<a
								className='circ'
								href='mailto:team@quizzach.com'
								aria-label={t('nav.contact')}
							>
								<IcMail size={18} />
							</a>
						</div>
					</div>
				</MobileSheet>
			)}

			<MobileNavBar aria-label={t('nav.bottomNavigation')}>
				<button
					type='button'
					className={`it ${isHomeActive ? 'active' : ''}`}
					aria-label={t('nav.homepage')}
					onClick={() => go(RoutePath.Homepage)}
				>
					<IcKjupitnik size={22} />
				</button>

				<button
					type='button'
					className={`it ${isProfileActive ? 'active' : ''}`}
					aria-label={t('nav.myQuizzes')}
					onClick={() => go(RoutePath.Profile)}
				>
					<IcCards size={22} />
				</button>

				<button
					type='button'
					className='newq'
					onClick={() => {
						if (showCreateMenu) closeOverlayMenus();
						else openOverlayMenu('create');
					}}
				>
					<IcPlus size={15} />
					{t('nav.newQuiz')}
				</button>

				<button
					type='button'
					className={`it ${isCartActive ? 'active' : ''}`}
					aria-label={t('nav.cart')}
					onClick={() => go(RoutePath.Summary)}
				>
					<span className='bwrap'>
						<IcBasket size={22} />
						{cartCount > 0 && (
							<span className='badge'>{cartCount > 9 ? '9+' : cartCount}</span>
						)}
					</span>
				</button>

				<button
					type='button'
					className={`it ${showMoreMenu ? 'active' : ''}`}
					aria-label={t('nav.more')}
					onClick={() => {
						if (showMoreMenu) closeOverlayMenus();
						else openOverlayMenu('more');
					}}
				>
					<span className='bwrap'>
						<IcMenuLines size={22} />
						{isAuthenticated && notifications.length > 0 && (
							<span className='ndot' />
						)}
					</span>
				</button>
			</MobileNavBar>
		</>
	);
};
